import { Globe } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import { Button } from '@/components/ui/button';

export interface PublishStoreCardProps {
  /** Slug of the store being considered for publish. Kept in the prop
   *  surface (not used for rendering) so the dashboard route can pass
   *  it without a prop-drilling refactor when the reassurance copy
   *  eventually references the URL. Reserved intentionally. */
  readonly storeSlug: string;
  /** Fires when the merchant clicks the primary CTA. The parent opens
   *  the `PublishStoreDialog` — pre-publish validation happens inside
   *  the dialog/hook, not here. */
  readonly onPublishClick: () => void;
}

/**
 * Draft-state dashboard card that invites the merchant to publish.
 *
 * Visual structure mirrors the Stitch `admin-dashboard-draft-state.html`
 * output:
 *   - Amiri-serif headline ("Ready to launch your store?")
 *   - muted subtitle
 *   - Primary `<Button variant="default">` with Globe icon + translated
 *     CTA label. The button is full-width so its touch target on 375px
 *     screens is comfortably above the 44px minimum (shadcn's default
 *     `size="default"` is 40px, so we widen with size="lg" + min-h-12).
 *
 * No pre-validation runs here — the inventory gate and the auth check
 * are server-side (`publish_store` RPC) and the `PublishStoreDialog`
 * surfaces the result. Keeping this card "pure presentation + one click
 * handler" keeps the testing surface small (shallow render + click).
 *
 * Test-ids (F-004 design §5.5):
 *   - `publish-store-card` on the root card
 *   - `publish-store-cta` on the primary button
 */
export function PublishStoreCard({ storeSlug: _storeSlug, onPublishClick }: PublishStoreCardProps) {
  const { t, dir } = useTranslation();

  return (
    <div
      dir={dir}
      data-testid="publish-store-card"
      className="border-border bg-card shadow-admin-card rounded-lg border p-6"
    >
      {/* Headline — Fraunces/Amiri display font to echo the warm
          traditional personality from DESIGN.md. text-start so it
          aligns to the reading edge in both LTR and RTL. */}
      <h2 className="font-display text-foreground text-start text-xl font-bold leading-tight">
        {t('admin.publishStore.draftCard.title')}
      </h2>

      {/* Subtitle — Sans (IBM Plex Sans Arabic in AR), muted for a
          quieter second-level voice. `text-start` for RTL correctness. */}
      <p className="text-muted-foreground mt-2 text-start text-sm leading-relaxed">
        {t('admin.publishStore.draftCard.description')}
      </p>

      {/* Primary CTA — full width, Zaatar Olive background, Paper foreground.
          size="lg" gives us h-11 (44px) which meets WCAG 2.5.5 Target
          Size. We add min-h-12 (48px) to match the Stitch spec exactly. */}
      <Button
        type="button"
        variant="default"
        size="lg"
        onClick={onPublishClick}
        data-testid="publish-store-cta"
        className="mt-4 min-h-12 w-full"
      >
        <Globe aria-hidden="true" className="size-5" strokeWidth={1.75} />
        <span>{t('admin.publishStore.ctaLabel')}</span>
      </Button>
    </div>
  );
}
